import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "../data";
import { Button } from "./Button";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-cream/85 border-b border-ink/10 shadow-[0_8px_30px_-12px_rgba(16,24,32,0.25)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-brand"
          className="font-display font-bold text-ink text-2xl md:text-3xl tracking-[0.12em] leading-none"
        >
          PROSPEC
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => {
            const active = n.to === pathname;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className="group relative font-mono text-xs uppercase tracking-[0.16em] text-ink py-1"
              >
                {n.label}
                <span
                  className={`absolute left-0 -bottom-0.5 h-px bg-gold transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Button to="/contact" data-testid="nav-cta" className="!px-5 !py-3">
            Health-check
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-ink p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-0 top-20 bg-ink text-cream px-6 py-12 flex flex-col"
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                    className="block font-display text-4xl py-3 text-cream border-b border-cream/10"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <Button to="/contact" variant="gold" className="mt-10 w-full">
              Book a free health-check
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
